import { Box, Typography, Chip, CircularProgress } from '@mui/material'
import { FiberManualRecord } from '@mui/icons-material'
import { COLORS } from '../../constants/colors'
import { TEXT } from '../../constants/text'
import { useSSE } from '../../hooks/useSSE'
import { useTier } from '../../hooks/useTier'
import { TIERS, TIER_CONFIG } from '../../constants/tiers'
import SignalCard from './SignalCard'
import TierGate from '../layout/TierGate'

const STATUS_MAP = {
  connecting: { color: COLORS.gold.primary, label: 'Connecting…' },
  open: { color: COLORS.green.primary, label: 'Live' },
  error: { color: COLORS.red.primary, label: 'Reconnecting…' },
}

export default function SignalFeed({ onUpgrade }) {
  const { tier, config } = useTier()
  const delayMs = config.limits.tickerDelay || 0

  const { signals, status } = useSSE('/api/signals/stream', { delayMs })

  const st = STATUS_MAP[status]

  // Split signals by type for gating
  // Backend types: NEW_TOKEN, RUG_ALERT, WHALE_ACCUMULATION, HIGH_CONVICTION, BUNDLE_DETECTED
  const WHALE_TYPES = new Set(['WHALE_ACCUMULATION'])
  const ELITE_TYPES = new Set(['HIGH_CONVICTION', 'BUNDLE_DETECTED'])
  const baseSignals = signals.filter((s) => !WHALE_TYPES.has(s.type) && !ELITE_TYPES.has(s.type))
  const whaleSignals = signals.filter((s) => WHALE_TYPES.has(s.type))
  const bossSignals = signals.filter((s) => ELITE_TYPES.has(s.type))

  return (
    <Box>
      {/* Title bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          {TEXT.FEED.TITLE}
        </Typography>
        <Chip
          icon={<FiberManualRecord sx={{ fontSize: '10px !important', color: `${st.color} !important` }} />}
          label={st.label}
          size="small"
          sx={{
            height: 24,
            fontSize: '0.7rem',
            fontWeight: 600,
            color: st.color,
            background: `${st.color}15`,
            border: `1px solid ${st.color}33`,
          }}
        />
        {delayMs > 0 && (
          <Chip
            label={`${delayMs / 1000}s delay`}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.65rem',
              color: COLORS.text.muted,
              background: COLORS.bg.glass,
              border: `1px solid ${COLORS.border.light}`,
            }}
          />
        )}
      </Box>

      {/* Feed list */}
      {signals.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={28} sx={{ color: COLORS.gold.primary, mb: 2 }} />
          <Typography sx={{ color: COLORS.text.muted, fontSize: '0.85rem' }}>
            {TEXT.FEED.EMPTY}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Always-visible signals */}
          {baseSignals.map((s, i) => (
            <SignalCard key={s.id || i} signal={s} />
          ))}

          {/* Whale alerts — require Pro */}
          {whaleSignals.length > 0 && (
            <TierGate requiredTier={TIERS.PRO} onUpgrade={onUpgrade}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: COLORS.blue.primary, mb: 1, mt: 2 }}>
                  🐋 Whale Alerts
                </Typography>
                {whaleSignals.map((s, i) => (
                  <SignalCard key={s.id || `w${i}`} signal={s} />
                ))}
              </Box>
            </TierGate>
          )}

          {/* Boss Man signals — require Elite */}
          {bossSignals.length > 0 && (
            <TierGate requiredTier={TIERS.ELITE} onUpgrade={onUpgrade}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: COLORS.gold.primary, mb: 1, mt: 2 }}>
                  👑 Boss Man Signals
                </Typography>
                {bossSignals.map((s, i) => (
                  <SignalCard key={s.id || `b${i}`} signal={s} />
                ))}
              </Box>
            </TierGate>
          )}
        </Box>
      )}
    </Box>
  )
}
